import { NgFor, NgIf } from '@angular/common';
import { Component, Input, Optional } from '@angular/core';
import { IconComponent, TypographyComponent } from 'components';
import { StarRatingBarComponent } from '@brand-lolly/app/social-media-influencer/modules/social-listening/components/star-rating-bar/star-rating-bar.component';
import { ReportBlockData } from '../../../../services/report-blocks.service';
import { ReportLanguageService } from '../../../../services/report-language.service';
import { reportMessage } from '../../../../services/report-messages';
import { normalizeThemeColor } from '../../../../services/report-preview-utils';

/** One platform's tile, prepared once so the template only reads fields. */
interface RatingTile {
	platformId: string;
	platform: string;
	/**
	 * Its average, "4.3". Null when its state stands in the rating's place;
	 * a dash when only unrated reviews were collected for it.
	 */
	rating: string | null;
	/** "25 reviews", dated in the period; null when there are none. */
	count: string | null;
	/** How its collection went, or that the period holds none of its reviews. */
	note: string | null;
}

interface StarBar {
	stars: number;
	count: number;
}

/**
 * Reviews and ratings drawn as rating bars: each connected platform's average
 * and count of the reviews dated in the period, then those reviews over one
 * to five stars as the Competitors and Industry pages draw them.
 *
 * Every figure is the backend's (`blocks/review_ratings.py`), counted from the
 * period's reviews and never a platform's lifetime rating, and every state a
 * platform is in comes written in the report's language. A platform with
 * nothing to rate says its state where its rating would be.
 */
@Component({
	standalone: true,
	selector: 'app-report-review-ratings',
	imports: [
		NgFor,
		NgIf,
		IconComponent,
		TypographyComponent,
		StarRatingBarComponent
	],
	templateUrl: './report-review-ratings.component.html',
	styleUrls: ['./report-review-ratings.component.scss']
})
export class ReportReviewRatingsComponent {
	@Input() set block(value: ReportBlockData | null) {
		this.build(value);
	}

	/** The report's accent, for the stars and the bars; null keeps the theme's. */
	@Input() set themeColor(value: string | null) {
		this.accent = normalizeThemeColor(value);
		this.starColor = this.accent ?? 'var(--report-accent, #2872e1)';
	}

	tiles: RatingTile[] = [];
	bars: StarBar[] = [];
	maxCount = 0;
	distributionLabel = '';
	accent: string | null = null;
	starColor = 'var(--report-accent, #2872e1)';

	constructor(
		@Optional() private reportLanguage: ReportLanguageService | null
	) {}

	trackByPlatform(_index: number, tile: RatingTile): string {
		return tile.platformId;
	}

	trackByStars(_index: number, bar: StarBar): number {
		return bar.stars;
	}

	private build(block: ReportBlockData | null): void {
		const language = this.reportLanguage?.language;
		this.tiles = (block?.rows ?? []).map(row => {
			const rating = row['rating'];
			const reviews = Number(row['reviews'] ?? 0);
			const note = row['note'] ? String(row['note']) : null;
			return {
				platformId: String(row['platform_id'] ?? row['platform'] ?? ''),
				platform: String(row['platform'] ?? ''),
				rating:
					typeof rating === 'number'
						? rating.toFixed(1)
						: note
						? null
						: '—',
				count: reviews
					? reportMessage(language, 'reviews.count', {
							count: reviews
					  })
					: null,
				note
			};
		});
		this.bars = (block?.distribution ?? []).map(bucket => ({
			stars: bucket.stars,
			count: bucket.count
		}));
		this.maxCount = Math.max(0, ...this.bars.map(bar => bar.count));
		this.distributionLabel = reportMessage(
			language,
			'reviews.distribution'
		);
	}
}
